import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box, Card, Typography, Button, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Select, MenuItem, FormControl, InputLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Stack, Chip, Tab, Tabs, Checkbox, Toolbar, Autocomplete,
  Tooltip, alpha, FormControlLabel, LinearProgress, List, ListItem,
  ListItemText, ListItemSecondaryAction, Divider, Alert, CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, ArrowForward as CarryIcon,
  Search as SearchIcon, SmartToy as AiIcon, Send as SendIcon, Repeat as RepeatIcon,
  FileDownload as FileDownloadIcon, AttachFile as AttachFileIcon, Block as BlockIcon,
  Download as DownloadIcon, Schedule as ScheduleIcon, CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import { workItemsApi, categoriesApi, tagsApi, authApi, projectsApi, driversApi, exportApi, rhythmApi } from '../api/services';
import type { WorkItem, WorkItemAttachment, WorkItemType, Priority, WorkItemStatus, WorkItemFlag, Category, Tag, Project, AnnualStrategicDriver } from '../types/models';
import PriorityChip from '../components/PriorityChip';
import StatusChip from '../components/StatusChip';
import HelpPanel from '../components/HelpPanel';
import DriverPill from '../components/DriverPill';
import { WorkItemTypeBadge } from '../components/workItems/WorkItemTypeTreatment';

const todayStr = format(new Date(), 'yyyy-MM-dd');

function getCarryForwardDays(item: WorkItem): number | null {
  if (!item.isCarryForward || !item.originalDate) return null;
  return Math.ceil((new Date().getTime() - new Date(item.originalDate).getTime()) / 86400000);
}

const FLAG_OPTIONS: WorkItemFlag[] = [
  'Leadership', 'Dependency', 'Urgent', 'Informational',
  'FollowUp', 'DecisionRequired',
];

const workItemTypes: WorkItemType[] = ['Task', 'Decision', 'Briefing', 'Review', 'FollowUp', 'Draft', 'Reading'];
const priorities: Priority[] = ['Critical', 'High', 'Medium', 'Low'];
const statuses: WorkItemStatus[] = ['Backlog', 'Planned', 'InProgress', 'Done', 'Deferred', 'Cancelled'];
const recurrencePatterns = ['Daily', 'Weekly', 'Biweekly', 'Monthly', 'Quarterly'];
const weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function WorkItemsPage() {
  const location = useLocation();
  const [items, setItems] = useState<WorkItem[]>([]);
  const [loading, setLoading] = useState(true); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState<Partial<WorkItem> | null>(null);
  const [viewItem, setViewItem] = useState<WorkItem | null>(null);
  const [tabValue, setTabValue] = useState(0);

  // Bulk selection
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Search & filter
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('');
  const [filterPriority, setFilterPriority] = useState<string>('');

  // Categories & tags for the create/edit dialog
  const [categories, setCategories] = useState<Category[]>([]);
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [editTagNames, setEditTagNames] = useState<string[]>([]);
  const [editFlags, setEditFlags] = useState<string[]>([]);

  // Delegation state
  const [delegateDialogOpen, setDelegateDialogOpen] = useState(false);
  const [delegateItem, setDelegateItem] = useState<WorkItem | null>(null);
  const [delegateTarget, setDelegateTarget] = useState('');
  const [delegateDate, setDelegateDate] = useState('');
  const [delegateNotes, setDelegateNotes] = useState('');
  const [executives, setExecutives] = useState<{ id: string; displayName: string; email: string }[]>([]);
  const [activeProjects, setActiveProjects] = useState<Project[]>([]);
  const [strategicDrivers, setStrategicDrivers] = useState<AnnualStrategicDriver[]>([]);

  // Attachments state
  const [attachments, setAttachments] = useState<WorkItemAttachment[]>([]);
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Slot suggestions
  const [slotSuggestions, setSlotSuggestions] = useState<Array<{ startTime: string; endTime: string; score: number; reason: string }>>([]);
  const [suggestingSlots, setSuggestingSlots] = useState(false);
  const [schedulingItem, setSchedulingItem] = useState(false);
  const [scheduleSuccess, setScheduleSuccess] = useState<string | null>(null);

  const loadItems = async () => {
    setLoading(true);
    try {
      const data = await workItemsApi.getAll();
      setItems(data);
    } catch (err) {
      console.error('Failed to load work items', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadItems(); }, []);

  // Load categories, tags, executives, projects, and drivers once
  useEffect(() => {
    categoriesApi.getAll().then(setCategories).catch(() => {});
    tagsApi.getAll().then(setAllTags).catch(() => {});
    authApi.getUsers().then(users => {
      setExecutives(users.filter((u: any) => u.role === 'Executive'));
    }).catch(() => {});
    projectsApi.getAll({ status: 'Active' }).then(setActiveProjects).catch(() => {});
    driversApi.getAll().then(setStrategicDrivers).catch(() => {});
  }, []);

  // Open create dialog when navigated with ?action=create
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('action') === 'create' && !dialogOpen) {
      setEditItem({
        title: '', type: 'Task', priority: 'Medium', status: 'Backlog',
        estimatedMinutes: 30, targetDate: format(new Date(), 'yyyy-MM-dd'),
        flags: [],
      });
      setEditTagNames([]);
      setEditFlags([]);
      setAttachments([]);
      setSlotSuggestions([]);
      setDialogOpen(true);
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Attachment helpers
  const loadAttachments = useCallback(async (workItemId: string) => {
    try {
      const data = await workItemsApi.getAttachments(workItemId);
      setAttachments(data);
    } catch {
      setAttachments([]);
    }
  }, []);

  const handleUploadAttachment = async (file: File) => {
    if (!editItem?.id) return;
    if (file.size > 5 * 1024 * 1024) {
      alert('File size exceeds 5 MB limit.');
      return;
    }
    if (attachments.length >= 10) {
      alert('Maximum 10 attachments per work item.');
      return;
    }
    setAttachmentUploading(true);
    try {
      await workItemsApi.uploadAttachment(editItem.id, file);
      await loadAttachments(editItem.id);
    } catch (err) {
      console.error('Failed to upload attachment', err);
      alert('Failed to upload attachment.');
    } finally {
      setAttachmentUploading(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    if (!editItem?.id) return;
    if (!window.confirm('Delete this attachment?')) return;
    try {
      await workItemsApi.deleteAttachment(editItem.id, attachmentId);
      await loadAttachments(editItem.id);
    } catch (err) {
      console.error('Failed to delete attachment', err);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Slot suggestion helpers
  const handleSuggestSlots = async () => {
    if (!editItem?.estimatedMinutes || !editItem?.targetDate) return;
    setSuggestingSlots(true);
    setSlotSuggestions([]);
    try {
      const result = await rhythmApi.suggestSlots(
        editItem.estimatedMinutes,
        editItem.targetDate,
        editItem.priority?.toLowerCase()
      );
      setSlotSuggestions(result.suggestions || []);
    } catch (err) {
      console.error('Failed to suggest slots', err);
    } finally {
      setSuggestingSlots(false);
    }
  };

  const handleScheduleItem = async (workItemId: string, targetDate?: string) => {
    setSchedulingItem(true);
    setScheduleSuccess(null);
    try {
      const date = targetDate || editItem?.targetDate || todayStr;
      const result = await rhythmApi.autoSchedule({ date, workItemIds: [workItemId] });
      if (result.createdBlocks.length > 0) {
        const block = result.createdBlocks[0];
        const start = new Date(block.startTime);
        const end = new Date(block.endTime);
        const timeStr = `${start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
        setScheduleSuccess(`Scheduled for ${timeStr}`);
      } else if (result.conflicts.length > 0) {
        setScheduleSuccess(result.conflicts[0]);
      } else {
        setScheduleSuccess(result.message);
      }
      setSlotSuggestions([]);
      await loadItems();
    } catch (err) {
      console.error('Failed to schedule work item', err);
    } finally {
      setSchedulingItem(false);
    }
  };

  // Dependency helpers
  const getBlockingItems = useCallback((itemId: string): WorkItem[] => {
    return items.filter(i => i.blockedByWorkItemId === itemId);
  }, [items]);

  const handleSave = async () => {
    if (!editItem?.title) return;
    try {
      const payload = {
        ...editItem,
        flags: editFlags as WorkItemFlag[],
        // Clear recurrence fields if not recurring
        ...(!editItem.isRecurring && {
          recurrencePattern: undefined,
          recurrenceDays: undefined,
          recurrenceEndDate: undefined,
        }),
      };
      if (editItem.id) {
        await workItemsApi.update(editItem.id, payload);
      } else {
        await workItemsApi.create(payload);
      }
      setDialogOpen(false);
      setEditItem(null);
      setEditFlags([]);
      loadItems();
    } catch (err) {
      console.error('Failed to save work item', err);
    }
  };

  const handleOpenDelegate = (item: WorkItem) => {
    setDelegateItem(item);
    setDelegateTarget('');
    setDelegateDate(item.targetDate || '');
    setDelegateNotes('');
    setDelegateDialogOpen(true);
  };

  const handleDelegate = async () => {
    if (!delegateItem || !delegateTarget) return;
    try {
      await workItemsApi.delegate(delegateItem.id, {
        targetExecutiveUserId: delegateTarget,
        targetDate: delegateDate || undefined,
        notes: delegateNotes || undefined,
      });
      setDelegateDialogOpen(false);
      setDelegateItem(null);
      loadItems();
    } catch (err) {
      console.error('Failed to delegate work item', err);
    }
  };

  const handleComplete = async (id: string) => {
    try {
      await workItemsApi.update(id, { status: 'Done' });
      loadItems();
    } catch (err) {
      console.error('Failed to complete', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this work item?')) return;
    try {
      await workItemsApi.delete(id);
      loadItems();
    } catch (err) {
      console.error('Failed to delete', err);
    }
  };

  const handleCarryForward = async (id: string) => {
    const tomorrow = format(new Date(Date.now() + 86400000), 'yyyy-MM-dd');
    try {
      await workItemsApi.carryForward(id, tomorrow);
      loadItems();
    } catch (err) {
      console.error('Failed to carry forward', err);
    }
  };

  // Bulk actions
  const handleBulkCarryForward = async () => {
    const tomorrow = format(new Date(Date.now() + 86400000), 'yyyy-MM-dd');
    try {
      await Promise.all(Array.from(selected).map(id => workItemsApi.carryForward(id, tomorrow)));
      setSelected(new Set());
      loadItems();
    } catch (err) {
      console.error('Bulk carry forward failed', err);
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.size} selected work items?`)) return;
    try {
      await Promise.all(Array.from(selected).map(id => workItemsApi.delete(id)));
      setSelected(new Set());
      loadItems();
    } catch (err) {
      console.error('Bulk delete failed', err);
    }
  };

  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (ids: string[]) => {
    const allSelected = ids.every(id => selected.has(id));
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(ids));
    }
  };

  // Tab filter — Today is now first (index 0)
  const todayItems = items.filter(i => i.targetDate === todayStr);
  const tabFiltered = tabValue === 0
    ? todayItems
    : tabValue === 1
      ? items.filter(i => !['Done', 'Cancelled'].includes(i.status))
      : tabValue === 2
        ? items.filter(i => i.status === 'Done')
        : items;

  // Search & filter
  const filteredItems = useMemo(() => {
    let result = tabFiltered;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(i =>
        i.title.toLowerCase().includes(q) || (i.description || '').toLowerCase().includes(q)
      );
    }
    if (filterType) result = result.filter(i => i.type === filterType);
    if (filterPriority) result = result.filter(i => i.priority === filterPriority);
    return result;
  }, [tabFiltered, searchQuery, filterType, filterPriority]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleExportCsv = async () => {
    try {
      const response = await exportApi.downloadWorkItemsCsv();
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `work-items-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert('Failed to export work items.');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Work Items</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={handleExportCsv}>
            Export CSV
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => {
            setEditItem({
              title: '', type: 'Task', priority: 'Medium', status: 'Backlog',
              estimatedMinutes: 30, targetDate: format(new Date(), 'yyyy-MM-dd'),
              flags: [],
            });
            setEditTagNames([]);
            setEditFlags([]);
            setAttachments([]);
            setSlotSuggestions([]);
            setDialogOpen(true);
          }}>
            Add Work Item
          </Button>
        </Stack>
      </Box>

      <HelpPanel title="About Work Items" storageKey="workitems">
        Work items are tasks, decisions, briefings, and reviews. Use flags to mark urgency. Drag to reorder priority.
      </HelpPanel>

      <Tabs value={tabValue} onChange={(_, v) => { setTabValue(v); setSelected(new Set()); }} sx={{ mb: 2 }}>
        <Tab label={`Today (${items.filter(i => i.targetDate === todayStr).length})`} />
        <Tab label={`Active (${items.filter(i => !['Done', 'Cancelled'].includes(i.status)).length})`} />
        <Tab label={`Completed (${items.filter(i => i.status === 'Done').length})`} />
        <Tab label={`All (${items.length})`} />
      </Tabs>

      {/* Search & filter bar */}
      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap">
        <TextField
          size="small"
          placeholder="Search title or description..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          InputProps={{ startAdornment: <SearchIcon sx={{ mr: 0.5, fontSize: 18, color: 'text.secondary' }} /> }}
          sx={{ minWidth: 240 }}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Type</InputLabel>
          <Select value={filterType} label="Type" onChange={e => setFilterType(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {workItemTypes.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Priority</InputLabel>
          <Select value={filterPriority} label="Priority" onChange={e => setFilterPriority(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {priorities.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
          </Select>
        </FormControl>
        {(searchQuery || filterType || filterPriority) && (
          <Button size="small" onClick={() => { setSearchQuery(''); setFilterType(''); setFilterPriority(''); }}>
            Clear
          </Button>
        )}
      </Stack>

      {/* Bulk actions toolbar */}
      {selected.size > 0 && (
        <Toolbar
          sx={{
            mb: 1, borderRadius: 2, minHeight: 48,
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
            px: 2,
          }}
        >
          <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
            {selected.size} selected
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="outlined" startIcon={<CarryIcon />} onClick={handleBulkCarryForward}>
              Carry Forward All
            </Button>
            <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={handleBulkDelete}>
              Delete Selected
            </Button>
          </Stack>
        </Toolbar>
      )}

      <TableContainer component={Card}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selected.size > 0 && selected.size < filteredItems.length}
                  checked={filteredItems.length > 0 && selected.size === filteredItems.length}
                  onChange={() => toggleSelectAll(filteredItems.map(i => i.id))}
                  size="small"
                />
              </TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Flags</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredItems.map((item) => (
              <TableRow key={item.id} hover selected={selected.has(item.id)} sx={{ cursor: 'pointer', opacity: item.status === 'Done' || item.status === 'Cancelled' ? 0.5 : 1 }} onClick={() => setViewItem(item)}>
                <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                  <Checkbox size="small" checked={selected.has(item.id)} onChange={() => toggleSelect(item.id)} />
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      sx={{
                        textDecoration: item.status === 'Done' ? 'line-through' : item.status === 'Cancelled' ? 'line-through' : 'none',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0,
                      }}
                    >
                      {item.status === 'Done' && '✓ '}{item.title}
                    </Typography>
                    <DriverPill driver={item.strategicDriver} size="compact" />
                  </Stack>
                  {item.description && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {item.description.substring(0, 80)}{item.description.length > 80 ? '...' : ''}
                    </Typography>
                  )}
                </TableCell>
                <TableCell><WorkItemTypeBadge type={item.type} variant="full" /></TableCell>
                <TableCell><PriorityChip priority={item.priority} /></TableCell>
                <TableCell><StatusChip status={item.status} /></TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <span>{item.estimatedMinutes}m</span>
                    {item.aiEstimatedMinutes != null && item.aiEstimatedMinutes !== item.estimatedMinutes && (
                      <Tooltip title={`AI estimated: ${item.aiEstimatedMinutes}m`} arrow>
                        <Chip
                          icon={<AiIcon sx={{ fontSize: 14 }} />}
                          label={`${item.aiEstimatedMinutes}m`}
                          size="small"
                          variant="outlined"
                          color="info"
                          sx={{ height: 20, fontSize: '0.65rem', '& .MuiChip-icon': { fontSize: 12 } }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell>{item.targetDate}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {item.isCarryForward && (() => {
                      const days = getCarryForwardDays(item);
                      return (
                        <Chip
                          label={days != null ? `Carried ${days} day${days !== 1 ? 's' : ''}` : 'Carried'}
                          size="small"
                          color={days != null && days > 3 ? 'error' : 'warning'}
                          variant="outlined"
                          sx={{ height: 22, fontSize: '0.65rem' }}
                        />
                      );
                    })()}
                    {item.isRecurring && !item.recurrenceParentId && (
                      <Chip
                        icon={<RepeatIcon sx={{ fontSize: 12 }} />}
                        label={item.recurrencePattern || 'Recurring'}
                        size="small"
                        color="info"
                        variant="outlined"
                        sx={{ height: 22, fontSize: '0.65rem', '& .MuiChip-icon': { fontSize: 12 } }}
                      />
                    )}
                    {item.recurrenceParentId && (
                      <Chip
                        icon={<RepeatIcon sx={{ fontSize: 12 }} />}
                        label="Instance"
                        size="small"
                        variant="outlined"
                        sx={{ height: 22, fontSize: '0.65rem', '& .MuiChip-icon': { fontSize: 12 }, color: 'text.secondary' }}
                      />
                    )}
                    {item.blockedByWorkItemId && (
                      <Tooltip title={`Blocked by: ${items.find(i => i.id === item.blockedByWorkItemId)?.title || 'Unknown'}`} arrow>
                        <Chip
                          icon={<BlockIcon sx={{ fontSize: 12 }} />}
                          label="Blocked"
                          size="small"
                          color="error"
                          variant="outlined"
                          sx={{ height: 22, fontSize: '0.65rem', '& .MuiChip-icon': { fontSize: 12 } }}
                        />
                      </Tooltip>
                    )}
                    {getBlockingItems(item.id).length > 0 && (
                      <Tooltip title={`Blocking: ${getBlockingItems(item.id).map(i => i.title).join(', ')}`} arrow>
                        <Chip
                          icon={<BlockIcon sx={{ fontSize: 12 }} />}
                          label={`Blocking ${getBlockingItems(item.id).length}`}
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ height: 22, fontSize: '0.65rem', '& .MuiChip-icon': { fontSize: 12 } }}
                        />
                      </Tooltip>
                    )}
                    {item.flags?.filter(f => f !== 'CarryForward').map(f => (
                      <Chip key={f} label={f} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                    ))}
                  </Stack>
                </TableCell>
                <TableCell align="right" onClick={e => e.stopPropagation()}>
                  {item.status !== 'Done' && item.status !== 'Cancelled' && (
                    <Tooltip title="Mark Complete">
                      <IconButton size="small" color="success" onClick={() => handleComplete(item.id)}><CheckCircleIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  )}
                  <IconButton size="small" onClick={() => { setEditItem(item); setEditTagNames(item.workItemTags?.map(wt => wt.tag?.name || '').filter(Boolean) as string[] || []); setEditFlags(item.flags?.filter(f => !['CarryForward', 'ImageDerived'].includes(f)) || []); loadAttachments(item.id); setDialogOpen(true); }}><EditIcon fontSize="small" /></IconButton>
                  <Tooltip title="Carry Forward to Tomorrow">
                    <IconButton size="small" onClick={() => handleCarryForward(item.id)}><CarryIcon fontSize="small" /></IconButton>
                  </Tooltip>
                  <Tooltip title="Delegate">
                    <IconButton size="small" onClick={() => handleOpenDelegate(item)}><SendIcon fontSize="small" /></IconButton>
                  </Tooltip>
                  <IconButton size="small" onClick={() => handleDelete(item.id)}><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            {filteredItems.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography color="text.secondary" sx={{ py: 4 }}>No work items</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editItem?.id ? 'Edit Work Item' : 'New Work Item'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Title" fullWidth value={editItem?.title || ''} onChange={e => setEditItem(prev => ({ ...prev, title: e.target.value }))} />
            <TextField label="Description" fullWidth multiline rows={3} value={editItem?.description || ''} onChange={e => setEditItem(prev => ({ ...prev, description: e.target.value }))} />
            <Stack direction="row" spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select value={editItem?.type || 'Task'} label="Type" onChange={e => setEditItem(prev => ({ ...prev, type: e.target.value as WorkItemType }))}>
                  {workItemTypes.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select value={editItem?.priority || 'Medium'} label="Priority" onChange={e => setEditItem(prev => ({ ...prev, priority: e.target.value as Priority }))}>
                  {priorities.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                </Select>
              </FormControl>
            </Stack>
            <Stack direction="row" spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select value={editItem?.status || 'Backlog'} label="Status" onChange={e => setEditItem(prev => ({ ...prev, status: e.target.value as WorkItemStatus }))}>
                  {statuses.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField label="Duration (min)" type="number" fullWidth value={editItem?.estimatedMinutes || 30} onChange={e => setEditItem(prev => ({ ...prev, estimatedMinutes: parseInt(e.target.value) || 30 }))} />
            </Stack>
            <TextField label="Target Date" type="date" fullWidth InputLabelProps={{ shrink: true }} value={editItem?.targetDate || ''} onChange={e => { setEditItem(prev => ({ ...prev, targetDate: e.target.value })); setSlotSuggestions([]); }} />
            {editItem?.estimatedMinutes && editItem?.targetDate && (
              <Box>
                <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ScheduleIcon />}
                    onClick={handleSuggestSlots}
                    disabled={suggestingSlots || schedulingItem}
                  >
                    {suggestingSlots ? 'Finding slots...' : 'Suggest Time'}
                  </Button>
                  {editItem?.id && (
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={schedulingItem ? <CircularProgress size={14} color="inherit" /> : <ScheduleIcon />}
                      onClick={() => handleScheduleItem(editItem!.id!)}
                      disabled={schedulingItem || suggestingSlots}
                    >
                      {schedulingItem ? 'Scheduling...' : 'Schedule'}
                    </Button>
                  )}
                </Stack>
                {scheduleSuccess && (
                  <Alert severity="success" sx={{ mb: 1 }} onClose={() => setScheduleSuccess(null)}>
                    {scheduleSuccess}
                  </Alert>
                )}
                {suggestingSlots && <LinearProgress sx={{ mb: 1 }} />}
                {slotSuggestions.length > 0 && (
                  <Stack spacing={0.5}>
                    {slotSuggestions.map((slot, idx) => {
                      const start = new Date(slot.startTime);
                      const end = new Date(slot.endTime);
                      const timeStr = `${start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
                      const scorePercent = Math.round(slot.score * 100);
                      return (
                        <Card
                          key={idx}
                          variant="outlined"
                          sx={{
                            p: 1, cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' },
                            borderColor: idx === 0 ? 'primary.main' : 'divider',
                          }}
                          onClick={() => {
                            const dateStr = format(start, 'yyyy-MM-dd');
                            setEditItem(prev => ({ ...prev, targetDate: dateStr }));
                            setSlotSuggestions([]);
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Box>
                              <Typography variant="body2" fontWeight={600}>{timeStr}</Typography>
                              <Typography variant="caption" color="text.secondary">{slot.reason}</Typography>
                            </Box>
                            <Chip
                              label={`${scorePercent}%`}
                              size="small"
                              color={scorePercent >= 80 ? 'success' : scorePercent >= 60 ? 'warning' : 'default'}
                              sx={{ fontWeight: 600 }}
                            />
                          </Stack>
                        </Card>
                      );
                    })}
                    <Typography variant="caption" color="text.secondary">
                      Click a slot to set as target date
                    </Typography>
                  </Stack>
                )}
              </Box>
            )}
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={editItem?.categoryId || ''}
                label="Category"
                onChange={e => {
                  const cat = categories.find(c => c.id === e.target.value);
                  setEditItem(prev => ({ ...prev, categoryId: e.target.value || null, category: cat?.name || '' }));
                }}
              >
                <MenuItem value="">None</MenuItem>
                {categories.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Project</InputLabel>
              <Select
                value={editItem?.projectId || ''}
                label="Project"
                onChange={e => {
                  const projectId = e.target.value || undefined;
                  const project = activeProjects.find(p => p.id === projectId);
                  setEditItem(prev => {
                    const next = { ...prev, projectId: projectId || undefined };
                    // Auto-set strategic driver if project has one and user hasn't set one yet
                    if (project?.strategicDriverId && !prev?.strategicDriverId) {
                      next.strategicDriverId = project.strategicDriverId;
                    }
                    return next;
                  });
                }}
              >
                <MenuItem value="">None</MenuItem>
                {activeProjects.map(p => <MenuItem key={p.id} value={p.id}>{p.title}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Strategic Driver</InputLabel>
              <Select
                value={editItem?.strategicDriverId || ''}
                label="Strategic Driver"
                onChange={e => setEditItem(prev => ({ ...prev, strategicDriverId: e.target.value || undefined }))}
              >
                <MenuItem value="">None</MenuItem>
                {strategicDrivers.map(d => <MenuItem key={d.id} value={d.id}>{d.title}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Assign To</InputLabel>
              <Select
                value={editItem?.assignedToExecutiveId || ''}
                label="Assign To"
                onChange={e => setEditItem(prev => ({ ...prev, assignedToExecutiveId: e.target.value || null }))}
              >
                <MenuItem value="">Unassigned</MenuItem>
                {executives.map(exec => (
                  <MenuItem key={exec.id} value={exec.id}>{exec.displayName} ({exec.email})</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Autocomplete
              multiple
              options={allTags.map(t => t.name)}
              value={editTagNames}
              onChange={(_, newVal) => setEditTagNames(newVal)}
              freeSolo
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip size="small" label={option} {...getTagProps({ index })} key={option} />
                ))
              }
              renderInput={(params) => <TextField {...params} label="Tags" placeholder="Add tags..." />}
            />
            <Autocomplete
              multiple
              options={FLAG_OPTIONS}
              value={editFlags}
              onChange={(_, v) => setEditFlags(v)}
              renderTags={(value, getTagProps) =>
                value.map((flag, i) => (
                  <Chip label={flag} size="small" {...getTagProps({ index: i })} key={flag} />
                ))
              }
              renderInput={(params) => <TextField {...params} label="Flags" placeholder="Add flags" />}
            />
            <TextField label="Notes" fullWidth multiline rows={2} value={editItem?.notes || ''} onChange={e => setEditItem(prev => ({ ...prev, notes: e.target.value }))} />

            {/* Recurrence */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={editItem?.isRecurring || false}
                  onChange={e => setEditItem(prev => ({
                    ...prev,
                    isRecurring: e.target.checked,
                    ...(!e.target.checked && { recurrencePattern: undefined, recurrenceDays: undefined, recurrenceEndDate: undefined }),
                  }))}
                />
              }
              label="Recurring"
            />
            {editItem?.isRecurring && (
              <Stack spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>Recurrence Pattern</InputLabel>
                  <Select
                    value={editItem?.recurrencePattern || ''}
                    label="Recurrence Pattern"
                    onChange={e => setEditItem(prev => ({ ...prev, recurrencePattern: e.target.value }))}
                  >
                    {recurrencePatterns.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                  </Select>
                </FormControl>
                {(editItem?.recurrencePattern === 'Weekly' || editItem?.recurrencePattern === 'Biweekly') && (
                  <Autocomplete
                    multiple
                    options={weekDays}
                    value={editItem?.recurrenceDays ? editItem.recurrenceDays.split(',').map(d => d.trim()).filter(Boolean) : []}
                    onChange={(_, newVal) => setEditItem(prev => ({ ...prev, recurrenceDays: newVal.join(',') }))}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip size="small" label={option} {...getTagProps({ index })} key={option} />
                      ))
                    }
                    renderInput={(params) => <TextField {...params} label="Days" placeholder="Select days..." />}
                  />
                )}
                <TextField
                  label="End Date (optional)"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={editItem?.recurrenceEndDate || ''}
                  onChange={e => setEditItem(prev => ({ ...prev, recurrenceEndDate: e.target.value || undefined }))}
                />
              </Stack>
            )}

            {/* Dependencies */}
            <Divider sx={{ my: 1 }} />
            <FormControl fullWidth>
              <InputLabel>Blocked By</InputLabel>
              <Select
                value={editItem?.blockedByWorkItemId || ''}
                label="Blocked By"
                onChange={async e => {
                  const newValue = e.target.value || undefined;
                  setEditItem(prev => ({ ...prev, blockedByWorkItemId: newValue }));
                  if (editItem?.id) {
                    try {
                      await workItemsApi.setBlockedBy(editItem.id, newValue || null);
                      loadItems();
                    } catch (err) {
                      console.error('Failed to set dependency', err);
                    }
                  }
                }}
              >
                <MenuItem value="">None</MenuItem>
                {items
                  .filter(i => i.id !== editItem?.id && !['Done', 'Cancelled'].includes(i.status))
                  .map(i => (
                    <MenuItem key={i.id} value={i.id}>{i.title}</MenuItem>
                  ))}
              </Select>
            </FormControl>

            {/* Attachments (only for existing items) */}
            {editItem?.id && (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AttachFileIcon fontSize="small" /> Attachments ({attachments.length}/10)
                </Typography>
                {attachments.length > 0 && (
                  <List dense disablePadding>
                    {attachments.map(att => (
                      <ListItem key={att.id} sx={{ pl: 0, pr: 8 }}>
                        <ListItemText
                          primary={att.fileName}
                          secondary={`${formatFileSize(att.fileSizeBytes)} - ${format(new Date(att.createdAt), 'MMM d, yyyy')}`}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                        <ListItemSecondaryAction>
                          <Tooltip title="Download">
                            <IconButton
                              size="small"
                              component="a"
                              href={workItemsApi.downloadAttachmentUrl(editItem.id!, att.id)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <IconButton size="small" onClick={() => handleDeleteAttachment(att.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                )}
                {attachmentUploading && <LinearProgress sx={{ my: 1 }} />}
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) handleUploadAttachment(file);
                    e.target.value = '';
                  }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AttachFileIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={attachmentUploading || attachments.length >= 10}
                >
                  Add Attachment (max 5 MB)
                </Button>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* View Detail Dialog */}
      <Dialog open={!!viewItem} onClose={() => setViewItem(null)} maxWidth="sm" fullWidth>
        {viewItem && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="h6" component="span" sx={{ flex: 1 }}>{viewItem.title}</Typography>
                <Chip label={viewItem.type} size="small" variant="outlined" />
                <Chip label={viewItem.priority} size="small" color={viewItem.priority === 'Critical' ? 'error' : viewItem.priority === 'High' ? 'warning' : 'default'} />
                <Chip label={viewItem.status} size="small" color={viewItem.status === 'Done' ? 'success' : viewItem.status === 'InProgress' ? 'info' : 'default'} />
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2}>
                {viewItem.status === 'Done' && (
                  <Alert severity="success" icon={false} sx={{ py: 0.5 }}>
                    <Typography variant="body2" fontWeight={600}>Completed</Typography>
                  </Alert>
                )}
                {viewItem.status === 'Cancelled' && (
                  <Alert severity="warning" icon={false} sx={{ py: 0.5 }}>
                    <Typography variant="body2" fontWeight={600}>Cancelled</Typography>
                  </Alert>
                )}
                {viewItem.description && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Description</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{viewItem.description}</Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Estimated</Typography>
                    <Typography variant="body2">{viewItem.estimatedMinutes}m</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Target Date</Typography>
                    <Typography variant="body2">{viewItem.targetDate || 'No target date'}</Typography>
                  </Box>
                  {viewItem.category && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>Category</Typography>
                      <Typography variant="body2">{viewItem.category}</Typography>
                    </Box>
                  )}
                </Box>
                {viewItem.notes && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Notes</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{viewItem.notes}</Typography>
                  </Box>
                )}
                {viewItem.flags && viewItem.flags.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Flags</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                      {viewItem.flags.map(f => <Chip key={f} label={f} size="small" variant="outlined" />)}
                    </Box>
                  </Box>
                )}
                {viewItem.isCarryForward && (
                  <Alert severity="info" sx={{ py: 0 }}>
                    Carried forward{viewItem.originalDate ? ` from ${viewItem.originalDate}` : ''}
                  </Alert>
                )}
                {viewItem.isRecurring && (
                  <Alert severity="info" sx={{ py: 0 }}>
                    Recurring: {viewItem.recurrencePattern}{viewItem.recurrenceDays ? ` (${viewItem.recurrenceDays})` : ''}
                  </Alert>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setViewItem(null)}>Close</Button>
              {viewItem.status !== 'Done' && viewItem.status !== 'Cancelled' && (
                <Button variant="contained" color="success" startIcon={<CheckCircleIcon />} onClick={async () => {
                  await handleComplete(viewItem.id);
                  setViewItem(null);
                }}>Mark Complete</Button>
              )}
              <Button variant="outlined" onClick={() => {
                setEditItem(viewItem);
                setEditFlags(viewItem.flags?.filter(f => !['CarryForward', 'ImageDerived'].includes(f)) || []);
                loadAttachments(viewItem.id);
                setDialogOpen(true);
                setViewItem(null);
              }}>Edit</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Delegate Dialog */}
      <Dialog open={delegateDialogOpen} onClose={() => setDelegateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Delegate Work Item</DialogTitle>
        <DialogContent>
          {delegateItem && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Delegating: <strong>{delegateItem.title}</strong>
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Target Executive</InputLabel>
                <Select
                  value={delegateTarget}
                  label="Target Executive"
                  onChange={e => setDelegateTarget(e.target.value)}
                >
                  {executives.map(exec => (
                    <MenuItem key={exec.id} value={exec.id}>{exec.displayName} ({exec.email})</MenuItem>
                  ))}
                  {executives.length === 0 && (
                    <MenuItem disabled>No executives available</MenuItem>
                  )}
                </Select>
              </FormControl>
              <TextField
                label="New Target Date (optional)"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={delegateDate}
                onChange={e => setDelegateDate(e.target.value)}
              />
              <TextField
                label="Notes (optional)"
                fullWidth
                multiline
                rows={2}
                value={delegateNotes}
                onChange={e => setDelegateNotes(e.target.value)}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDelegateDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleDelegate} disabled={!delegateTarget}>
            Delegate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
